import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Wallet, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (cents, cur = "aud") => `$${((cents || 0) / 100).toFixed(2)} ${cur.toUpperCase()}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium" }) : "—");

export default function PayoutsPanel({ authHeaders }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setBusy(true);
    axios.get(`${API}/admin/payouts`, { headers: authHeaders, params: { days } })
      .then(({ data }) => setData(data))
      .finally(() => setBusy(false));
  }, [days, authHeaders]);

  return (
    <div className="space-y-6">
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2 inline-flex items-center gap-2">
            <Wallet size={14} /> Stripe Payout Reconciliation
          </div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            Every payout Stripe sends to Mama&apos;s bank, matched against our orders.
            Unmatched charges flag holes (e.g. paid via Stripe but never confirmed in our system).
          </p>
        </div>
        <div className="flex gap-1">
          {[14, 30, 60, 90].map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              data-testid={`payouts-range-${r}`}
              className={`text-[0.65rem] tracking-[0.22em] uppercase px-3 py-2 border transition-all ${
                days === r
                  ? "bg-[var(--mm-accent)] text-[var(--mm-bg)] border-[var(--mm-accent)]"
                  : "border-[var(--mm-line)] text-[var(--mm-cream-muted)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)]"
              }`}
            >
              {r}d
            </button>
          ))}
        </div>
      </div>

      {busy ? (
        <div className="py-16 text-center"><Loader2 size={24} className="mx-auto text-[var(--mm-accent)] animate-spin" /></div>
      ) : data?.error ? (
        <div className="p-5 border border-red-500/40 bg-red-500/10 text-sm text-red-300 inline-flex items-start gap-3">
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold mb-1">Stripe request failed</div>
            <div className="text-xs opacity-80">{data.error}</div>
            <div className="text-xs opacity-60 mt-2">This usually means the Stripe key isn&apos;t configured yet — payouts appear as soon as real transactions land.</div>
          </div>
        </div>
      ) : !data || data.payouts.length === 0 ? (
        <div className="py-16 text-center text-[var(--mm-cream-muted)] font-light">
          No Stripe payouts landed in this window yet. Payouts appear here once Stripe transfers money into Mama&apos;s bank.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4">
            <StatCard label="Payouts" value={data.totals.count} />
            <StatCard label="Paid to bank" value={money(data.totals.paid_cents)} />
            <StatCard label="In transit" value={money(data.totals.pending_cents)} />
          </div>
          <div className="space-y-3">
            {data.payouts.map((p) => (
              <div key={p.id} className="border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]/60">
                <button
                  onClick={() => setOpenId(openId === p.id ? null : p.id)}
                  className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-[var(--mm-line)]/20"
                  data-testid={`payout-toggle-${p.id}`}
                >
                  <div className="flex items-center gap-3">
                    {openId === p.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    <div>
                      <div className="font-display text-2xl text-[var(--mm-accent)]">{money(p.amount_cents, p.currency)}</div>
                      <div className="text-xs text-[var(--mm-cream-muted)] tracking-[0.2em] uppercase">
                        {p.status} · Arrives {p.arrival_date} · {p.matched_count} matched · {p.unknown_count} unknown
                      </div>
                    </div>
                  </div>
                  <div className="text-right text-xs text-[var(--mm-cream-muted)] font-mono">
                    {p.id.slice(0, 14)}…
                  </div>
                </button>

                {openId === p.id && (
                  <div className="border-t border-[var(--mm-line)]/60 p-4 space-y-4">
                    {p.matched.length > 0 && (
                      <ChargeSection title="Matched to our orders" rows={p.matched} tone="accent" />
                    )}
                    {p.unknown.length > 0 && (
                      <ChargeSection title="Unmatched (charged but no local order)" rows={p.unknown} tone="warning" />
                    )}
                    {p.matched.length === 0 && p.unknown.length === 0 && (
                      <div className="text-sm text-[var(--mm-cream-muted)]">No underlying charges — likely an internal transfer.</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ChargeSection({ title, rows, tone }) {
  const border = tone === "warning" ? "border-red-500/40" : "border-[var(--mm-accent)]/40";
  return (
    <div>
      <div className="text-[0.62rem] tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] mb-2">{title}</div>
      <div className={`border ${border}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[0.62rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] border-b border-[var(--mm-line)]/50">
              <th className="py-2 px-3">Order</th>
              <th className="py-2 px-3 text-right">Gross</th>
              <th className="py-2 px-3 text-right">Fee</th>
              <th className="py-2 px-3 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.balance_txn_id} className="border-b border-[var(--mm-line)]/30 text-[var(--mm-cream)]">
                <td className="py-2 px-3">
                  {r.matched_order ? (
                    <div>
                      <div className="font-medium">{r.matched_order.customer_name || "Guest"}</div>
                      <div className="text-[0.62rem] text-[var(--mm-cream-muted)]">
                        {(r.matched_order.order_type || "takeaway").toUpperCase()} · {(r.matched_order.session_id || "").slice(-8)}
                      </div>
                    </div>
                  ) : (
                    <div className="text-red-400 text-xs">
                      Unknown · {(r.payment_intent_id || "").slice(0, 18)}…
                    </div>
                  )}
                </td>
                <td className="py-2 px-3 text-right">{money(r.gross_cents)}</td>
                <td className="py-2 px-3 text-right text-[var(--mm-cream-muted)]">-{money(r.fee_cents)}</td>
                <td className="py-2 px-3 text-right font-semibold">{money(r.net_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
      <div className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] mb-2">{label}</div>
      <div className="font-display text-2xl text-[var(--mm-accent)]">{value}</div>
    </div>
  );
}
